// src/StoreBriefingPromptCard.jsx — Settings → Advanced → "Store Claude AI Prompt"
// (Eric, 2026-09-30). The instructions Claude follows when it writes each store's
// nightly Store Briefing — only the "What to write" part; the data description and the
// rules stay in code (Eric, 2026-09-30: a regional lead may edit this). Admin only (the Advanced tab and the endpoint both enforce it).
// The numbers and the page layout are fixed in code; this prompt shapes the wording,
// the priorities and the tone.
import React, { useEffect, useState } from 'react';
import { Card, BlockStack, InlineStack, Text, TextField, Button, Banner, Badge, Select, Divider, List, Box } from '@shopify/polaris';
import { authenticatedFetch } from './AuthComponent';

// **bold** in an action line → bold text (the same marker the Store Briefing page renders).
function Bolded({ text }) {
    return <>{String(text || '').split('**').map((part, i) => (i % 2 === 1 ? <Text as="span" fontWeight="bold" key={i}>{part}</Text> : <React.Fragment key={i}>{part}</React.Fragment>))}</>;
}

export default function StoreBriefingPromptCard() {
    const [prompt, setPrompt] = useState('');
    const [saved, setSaved] = useState('');
    const [defaultPrompt, setDefaultPrompt] = useState('');
    const [head, setHead] = useState('');     // fixed top of the prompt (read-only)
    const [rules, setRules] = useState('');   // fixed bottom of the prompt (read-only)
    const [isDefault, setIsDefault] = useState(true);
    const [model, setModel] = useState('');
    const [maxChars, setMaxChars] = useState(30000);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    // Test the prompt in the box (saved or not) on one store. Nothing is stored.
    const [stores, setStores] = useState([]);
    const [testStore, setTestStore] = useState('');
    const [testing, setTesting] = useState(false);
    const [testErr, setTestErr] = useState(null);
    const [testResult, setTestResult] = useState(null);
    // Settings → Briefing (Eric, 2026-10-05): run days, Team Notes and the Goals sheet live here too.
    const [runDays, setRunDays] = useState([0]);
    const [teamNotes, setTeamNotes] = useState('');
    const [savedNotes, setSavedNotes] = useState('');
    const [notesUpdated, setNotesUpdated] = useState(null);
    const [goalsUrl, setGoalsUrl] = useState('');
    const [goalsStatus, setGoalsStatus] = useState(null);
    const [goalsLoaded, setGoalsLoaded] = useState(null);
    const [importing, setImporting] = useState(false);
    const putSimple = async (payload, okText) => {
        setMsg(null);
        try {
            const r = await authenticatedFetch('/api/settings/store-briefing-prompt', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setMsg({ tone: 'success', text: okText });
            return j;
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); return null; }
    };
    const toggleRunDay = async (i) => {
        const next = runDays.includes(i) ? runDays.filter(d => d !== i) : [...runDays, i].sort();
        if (!next.length) { setMsg({ tone: 'critical', text: 'Keep at least one run day.' }); return; }
        setRunDays(next);
        await putSimple({ run_days: next }, `Briefings will be built on ${next.map(d => dayNames[d]).join(', ')}.`);
    };
    const saveNotes = async () => {
        const j = await putSimple({ team_notes: teamNotes }, 'Team Notes saved. They show on every store\u2019s briefing right away.');
        if (j) { setSavedNotes(j.team_notes || ''); setNotesUpdated(new Date().toISOString()); }
    };
    const saveGoalsUrl = async () => { await putSimple({ goals_url: goalsUrl }, 'Goals sheet link saved.'); };
    const uploadGoals = async (file) => {
        if (!file) return;
        setImporting(true); setMsg(null);
        try {
            const fd = new FormData(); fd.append('file', file, file.name);
            const r = await authenticatedFetch('/api/settings/store-briefing-goals/upload', { method: 'POST', body: fd });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setGoalsStatus(j); setGoalsLoaded({ days: j.rows, stores: j.stores, first_day: j.first_day, last_day: j.last_day });
            setMsg({ tone: 'success', text: `Goals loaded from ${file.name}: ${j.rows.toLocaleString()} store-days for ${j.stores} stores (${j.first_day} to ${j.last_day}).` });
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); }
        finally { setImporting(false); }
    };
    const importGoals = async () => {
        setImporting(true); setMsg(null);
        try {
            const r = await authenticatedFetch('/api/settings/store-briefing-goals/import', { method: 'POST' });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setGoalsStatus(j); setMsg({ tone: 'success', text: `Goals loaded: ${j.rows.toLocaleString()} store-days for ${j.stores} stores (${j.first_day} to ${j.last_day}).` });
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); }
        finally { setImporting(false); }
    };
    const [dayNames, setDayNames] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);

    useEffect(() => {
        authenticatedFetch('/api/reports/store_briefing/locations')
            .then(r => r.json()).then(j => setStores(j.locations || [])).catch(() => {});
    }, []);

    const runTest = async () => {
        setTesting(true); setTestErr(null); setTestResult(null);
        try {
            const r = await authenticatedFetch('/api/settings/store-briefing-prompt/test', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt, location_id: Number(testStore) }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.detail || (r.status === 504 ? 'The test took too long. Try again.' : `HTTP ${r.status}`));
            setTestResult(j);
        } catch (e) { setTestErr(e.message); }
        finally { setTesting(false); }
    };

    useEffect(() => {
        authenticatedFetch('/api/settings/store-briefing-prompt')
            .then(async r => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`); return j; })
            .then(j => {
                setPrompt(j.prompt || ''); setSaved(j.prompt || ''); setDefaultPrompt(j.default_prompt || ''); setHead(j.head || ''); setRules(j.rules || '');
                setIsDefault(!!j.is_default); setModel(j.model || ''); setMaxChars(j.max_chars || 30000); setLoaded(true);
                if (Array.isArray(j.day_names) && j.day_names.length === 7) setDayNames(j.day_names);
                if (Array.isArray(j.run_days)) setRunDays(j.run_days); setTeamNotes(j.team_notes || ''); setSavedNotes(j.team_notes || ''); setNotesUpdated(j.team_notes_updated_at || null);
                setGoalsUrl(j.goals_url || ''); setGoalsStatus(j.goals_status || null); setGoalsLoaded(j.goals_loaded || null);
            })
            .catch(e => setMsg({ tone: 'critical', text: `Could not load the prompt: ${e.message}` }));
    }, []);

    const save = async (text) => {
        setSaving(true); setMsg(null);
        try {
            const r = await authenticatedFetch('/api/settings/store-briefing-prompt', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt: text }),
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
            setPrompt(j.prompt); setSaved(j.prompt); setIsDefault(!!j.is_default);
            setMsg({ tone: 'success', text: j.is_default ? 'Back to the default prompt. It applies from tonight’s briefings.' : 'Prompt saved. It applies from tonight’s briefings.' });
        } catch (e) { setMsg({ tone: 'critical', text: e.message }); }
        finally { setSaving(false); }
    };

    const dirty = prompt !== saved;
    return (
        <Card>
            <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                    <Text variant="headingMd" as="h2">Store Briefing</Text>
                    {loaded && <Badge tone={isDefault ? undefined : 'attention'}>{isDefault ? 'Default prompt' : 'Customized prompt'}</Badge>}
                </InlineStack>
                <Divider />
                <Text variant="headingSm" as="h3">Team Notes</Text>
                <Text tone="subdued">
                    General notes and the things every store needs to do this week — the Multi Buy offer ending, a sale starting. They
                    appear near the top of every store’s briefing as soon as you save, until you change them. Blank lines separate
                    paragraphs; start lines with "-" for a list.
                </Text>
                <TextField label="Team Notes" labelHidden multiline={6} maxHeight={320} autoComplete="off" value={teamNotes} onChange={setTeamNotes}
                    disabled={!loaded} placeholder={'- The Multi Buy offer has ended: take down all signs and handouts.\n- The Fall Soccer Sale starts Thursday 10/8 and runs through Monday 10/12.'} />
                <InlineStack gap="200" blockAlign="center">
                    <Button variant="primary" onClick={saveNotes} disabled={!loaded || teamNotes === savedNotes}>Save Team Notes</Button>
                    <Button onClick={() => setTeamNotes(savedNotes)} disabled={teamNotes === savedNotes}>Discard</Button>
                    {notesUpdated && <Text tone="subdued" variant="bodySm">Last saved {new Date(notesUpdated).toLocaleString()}</Text>}
                </InlineStack>

                <Divider />
                <Text variant="headingSm" as="h3">Days the briefing is built</Text>
                <Text tone="subdued">The overnight job runs every day but only builds briefings on these days (sales through the day before).</Text>
                <InlineStack gap="400" wrap>
                    {dayNames.map((n, i) => (
                        <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input type="checkbox" checked={runDays.includes(i)} onChange={() => toggleRunDay(i)} disabled={!loaded} />
                            <span>{n}</span>
                        </label>
                    ))}
                </InlineStack>

                <Divider />
                <Text variant="headingSm" as="h3">Sales goals</Text>
                <Text tone="subdued">
                    The goals live in Operations Hub once loaded (one row per store per day), so the sheet is only needed to load new ones.
                    Two ways to load: the Google Sheet link (shared as "Anyone with the link: Viewer"; refreshed each time briefings are built),
                    or upload the sheet as an Excel file (File → Download → Microsoft Excel) with no sharing at all. One tab per month,
                    a row per store named like its Lightspeed shop ("MD Annapolis"), a column per day.
                </Text>
                <InlineStack gap="200" blockAlign="end" wrap>
                    <Box minWidth="420px">
                        <TextField label="Google Sheet link" value={goalsUrl} onChange={setGoalsUrl} autoComplete="off" disabled={!loaded} />
                    </Box>
                    <Button onClick={saveGoalsUrl} disabled={!loaded}>Save link</Button>
                    <Button onClick={importGoals} loading={importing} disabled={!loaded}>Load goals now</Button>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => { uploadGoals(e.target.files?.[0]); e.target.value = ''; }} disabled={!loaded || importing} />
                        <Button disabled={!loaded || importing}>Upload Excel file…</Button>
                    </label>
                </InlineStack>
                {goalsLoaded && (
                    <Text tone="subdued" variant="bodySm">
                        {goalsLoaded.days ? `${goalsLoaded.days.toLocaleString()} store-days of goals loaded for ${goalsLoaded.stores} stores, ${goalsLoaded.first_day} to ${goalsLoaded.last_day}.` : 'No goals loaded yet.'}
                        {goalsStatus && !goalsStatus.ok ? ` Last attempt ${goalsStatus.at}: ${goalsStatus.error}` : goalsStatus?.ok ? ` Last loaded ${goalsStatus.at}.` : ''}
                    </Text>
                )}

                <Divider />
                <Text variant="headingSm" as="h3">Test this prompt on one store</Text>
                <Text tone="subdued">
                    Runs the prompt in the box above, saved or not, against one store’s current numbers and shows what Claude writes. It takes
                    about 30 seconds. Nothing is saved, and the store’s live briefing does not change.
                </Text>
                <InlineStack gap="200" blockAlign="end">
                    <Box minWidth="260px">
                        <Select label="Store" options={[{ label: 'Pick a store', value: '' }, ...stores.map(l => ({ label: l.name, value: String(l.id) }))]}
                            value={testStore} onChange={setTestStore} />
                    </Box>
                    <Button onClick={runTest} loading={testing} disabled={!testStore || !prompt.trim()}>Test prompt</Button>
                </InlineStack>
                {testErr && <Banner tone="critical" onDismiss={() => setTestErr(null)}>{testErr}</Banner>}
                {testResult && (
                    <Box background="bg-surface-secondary" padding="400" borderRadius="200">
                        <BlockStack gap="300">
                            <Text tone="subdued">
                                Test result for {testResult.store}, sales through {testResult.as_of} · {testResult.model} ·{' '}
                                {Number(testResult.input_tokens || 0).toLocaleString()} tokens in, {Number(testResult.output_tokens || 0).toLocaleString()} out
                            </Text>
                            <Text as="p" variant="bodyLg">{testResult.narrative.brief}</Text>
                            <Text variant="headingSm" as="h4">What to do this week</Text>
                            <BlockStack gap="300">
                                {(testResult.narrative.todo || []).map((t, i) => (
                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', columnGap: 8 }}>
                                        <Text as="span" variant="headingSm" tone="subdued">{i + 1}.</Text>
                                        <BlockStack gap="100">
                                            <Text as="p" variant="headingSm">{t.title}</Text>
                                            {(t.points || []).length > 1
                                                ? <List>{t.points.map((pt, k) => <List.Item key={k}><Bolded text={pt} /></List.Item>)}</List>
                                                : (t.points || []).map((pt, k) => <Text as="p" key={k}><Bolded text={pt} /></Text>)}
                                        </BlockStack>
                                    </div>
                                ))}
                            </BlockStack>
                            {[['How sales are going', testResult.narrative.sales], ['Nearby stores', testResult.narrative.peers]].map(([h, list]) => (list || []).length > 0 && (
                                <BlockStack gap="100" key={h}>
                                    <Text variant="headingSm" as="h4">{h}</Text>
                                    <List>{list.map((x, k) => <List.Item key={k}>{x}</List.Item>)}</List>
                                </BlockStack>
                            ))}
                            {testResult.narrative.slow_intro && <Text as="p"><Text as="span" fontWeight="semibold">Stock that is not selling: </Text>{testResult.narrative.slow_intro}</Text>}
                            {testResult.narrative.pickups && <Text as="p"><Text as="span" fontWeight="semibold">Pickups: </Text>{testResult.narrative.pickups}</Text>}
                        </BlockStack>
                    </Box>
                )}
                <Divider />
                <Text variant="headingSm" as="h3">The prompt Claude follows</Text>
                <Text tone="subdued">
                    What Claude{model ? ` (${model})` : ''} is asked to write in each store’s Store Briefing, shown for reference. The whole
                    prompt is fixed: it describes the data, says what to write and sets the safety rules (use only the real numbers,
                    never invent an item). Ordering actions are always combined into one order list on the page itself.
                </Text>
                {/* One long box (Eric, 2026-10-06): the whole prompt, exactly as Claude receives it, read-only. */}
                <TextField
                    label="The full prompt (not editable)" labelHidden multiline={24} maxHeight={760} autoComplete="off" monospaced
                    value={[head, prompt, rules].filter(Boolean).join('\n\n')} disabled onChange={() => {}}
                />

            </BlockStack>
        </Card>
    );
}
